import {
  type BoxProps,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  type TaskQuery,
  type TaskStatus as TaskStatusType,
  TaskSystemStatusEnum,
  useTaskStatusesQuery,
  useUpdateTaskStatusMutation,
} from "@src/__generated__/urql-graphql";
import { TaskStatusBadge } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import type { TaskModel } from "@src/components/widgets/Modals/ModalCommunication/models";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { type FunctionComponent, useEffect } from "react";

type TaskStatusProps = {
  task: Pick<TaskQuery["task"], "id" | "status"> & {
    subtasks: Pick<TaskModel, "id" | "status">[] | undefined;
  };
  onChange?: (status: TaskStatusType) => void;
  showArrow?: "always" | "hover" | "never";
} & Omit<BoxProps, "onChange">;

export const TaskStatus: FunctionComponent<TaskStatusProps> = observer(
  function TaskStatus({ task, onChange, showArrow = "hover", ...props }) {
    const {
      taskDetailModalStore: store,
      tasksListingStore,
      UIStore,
    } = useStore();
    const [{ fetching: loadingUpdateStatus }, updateStatus] =
      useUpdateTaskStatusMutation();

    const [{ data }] = useTaskStatusesQuery({
      pause: store.taskStatuses.value.length > 0,
    });

    useEffect(() => {
      if (data) {
        store.taskStatuses.set(data.taskStatuses);
      }
    }, [data]);

    const handleChoiceListChange = (id: string) => {
      if (!task?.id) return;

      if (!canSetDoneStatus && doneStatus?.id === id) {
        UIStore.toast({
          status: "warning",
          title: t`This task cannot be marked as ${doneStatus.name.toLowerCase()} until all subtasks are completed.`,
        });
        return;
      }

      updateStatus({ task_id: task.id, status_id: id }).then(({ data }) => {
        if (!data?.updateTaskStatus) return;
        store.task.value?.setStatus(data.updateTaskStatus.status);
        tasksListingStore.updateTask(data.updateTaskStatus);
        onChange?.(data.updateTaskStatus.status);
      });
    };

    const doneStatus = computed(() => {
      return store.taskStatuses.value.find(
        (status) => status.system_status === TaskSystemStatusEnum.Done,
      );
    }).get();

    const canEditStatus = computed(() => {
      return (
        can("task_update_status_all") ||
        can({
          do: "task_update_status_own",
          eval: (user) => store.task.value?.createdBy?.id === user.id,
        })
      );
    }).get();

    const canSetDoneStatus: boolean = computed(() => {
      if (!task.subtasks) return true;
      return task.subtasks?.every(
        (subtask) => subtask.status.system_status === TaskSystemStatusEnum.Done,
      );
    }).get();

    const hasOtherTaskStatusOptions = computed(() => {
      return store.taskStatuses.value?.length > 0;
    }).get();

    if (!hasOtherTaskStatusOptions) {
      return (
        <TaskStatusBadge
          name={task.status.name}
          foreground_color={task.status.foreground_color}
          background_color={task.status.background_color}
        />
      );
    }

    return (
      <Menu isLazy>
        <MenuButton data-group disabled={!canEditStatus}>
          <HStack>
            <div>
              {loadingUpdateStatus && <Spinner color="teal.500" size="sm" />}
              {!loadingUpdateStatus && (
                <TaskStatusBadge
                  name={task.status.name}
                  foreground_color={task.status.foreground_color}
                  background_color={task.status.background_color}
                  {...props}
                />
              )}
            </div>
            {canEditStatus && showArrow !== "never" && (
              <Icon
                name="chevron-down"
                w="4"
                h="4"
                color="grey.500"
                display={showArrow === "always" ? "inline-flex" : "none"}
                _groupHover={{
                  display: showArrow !== "always" ? "inline-flex" : undefined,
                }}
              />
            )}
          </HStack>
        </MenuButton>
        <Portal>
          <MenuList>
            {store.taskStatuses.value.map((status) => (
              <MenuItem
                key={status.id}
                color={status.foreground_color}
                fontWeight="medium"
                onClick={() => handleChoiceListChange(status.id)}
              >
                {status.name}
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      </Menu>
    );
  },
);
